import { useCallback, useEffect, useRef, useState } from "react";
import type { LottoMapViewport, LottoStore } from "../../lotto-stores";

type NaverLatLng = new (latitude: number, longitude: number) => NaverLatLngValue;
interface NaverLatLngValue { lat(): number; lng(): number }
interface NaverPoint { readonly x: number; readonly y: number }
interface NaverMapProjection { fromCoordToOffset(point: NaverLatLngValue): NaverPoint }
interface NaverLatLngBounds { getSW(): NaverLatLngValue; getNE(): NaverLatLngValue }
interface NaverMapInstance {
  setCenter(point: NaverLatLngValue): void;
  setZoom(zoom: number): void;
  getZoom(): number;
  getBounds(): NaverLatLngBounds;
  getProjection(): NaverMapProjection;
}
interface NaverMarker { setMap(map: NaverMapInstance | null): void }
interface NaverMapsApi {
  readonly LatLng: NaverLatLng;
  readonly Map: new (element: HTMLElement, options: Record<string, unknown>) => NaverMapInstance;
  readonly Marker: new (options: Record<string, unknown>) => NaverMarker;
  readonly Event: { addListener(target: unknown, name: string, handler: () => void): unknown };
}

declare global {
  interface Window {
    naver?: { maps?: NaverMapsApi };
    navermap_authFailure?: () => void;
  }
}

type MapState = "loading" | "ready" | "unavailable" | "authentication-error" | "error";
type StoreMarkerTier = "RECENT_WINNER" | "NEARBY_BEST" | "FIRST_PRIZE" | "SECOND_PRIZE" | "OTHER";
type MapEntry =
  | { readonly kind: "store"; readonly store: LottoStore; readonly tier: StoreMarkerTier }
  | { readonly kind: "cluster"; readonly latitude: number; readonly longitude: number; readonly storeCount: number };

function sdkUrl(clientId: string): string {
  return `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(clientId)}`;
}

function loadSdk(clientId: string, retry: boolean, onAuthenticationFailure: () => void): Promise<NaverMapsApi> {
  if (window.naver?.maps) return Promise.resolve(window.naver.maps);
  return new Promise((resolve, reject) => {
    let settled = false;
    const deadline = Date.now() + 4_000;
    const waitForMaps = () => {
      if (settled) return;
      if (window.naver?.maps) {
        settled = true;
        resolve(window.naver.maps);
      } else if (Date.now() >= deadline) {
        settled = true;
        reject(new Error("naver_maps_missing"));
      } else {
        window.setTimeout(waitForMaps, 50);
      }
    };
    const previousAuthenticationFailure = window.navermap_authFailure;
    window.navermap_authFailure = () => {
      previousAuthenticationFailure?.();
      onAuthenticationFailure();
      if (settled) return;
      settled = true;
      reject(new Error("naver_maps_authentication_failed"));
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-appforge-naver-map="true"]');
    if (existing && retry) existing.remove();
    const pending = retry ? null : document.querySelector<HTMLScriptElement>('script[data-appforge-naver-map="true"]');
    if (pending) {
      if (pending.dataset.appforgeNaverMapClientId !== clientId) {
        reject(new Error("naver_maps_client_changed"));
        return;
      }
      pending.addEventListener("load", waitForMaps, { once: true });
      pending.addEventListener("error", () => { if (!settled) { settled = true; reject(new Error("naver_maps_load_failed")); } }, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = sdkUrl(clientId);
    script.async = true;
    script.defer = true;
    script.dataset.appforgeNaverMap = "true";
    script.dataset.appforgeNaverMapClientId = clientId;
    script.addEventListener("load", waitForMaps, { once: true });
    script.addEventListener("error", () => { if (!settled) { settled = true; reject(new Error("naver_maps_load_failed")); } }, { once: true });
    document.head.append(script);
  });
}

function markerContent(store: LottoStore, tier: StoreMarkerTier, selected: boolean, showCaption: boolean): string {
  const label = storeLabel(store, tier);
  const symbol = tier === "NEARBY_BEST" ? "★" : "₩";
  const selection = selected ? " selected" : "";
  const featured = tier === "RECENT_WINNER" || tier === "NEARBY_BEST" ? " featured" : "";
  const compact = showCaption ? "" : " compact";
  return `<span class="lotto-map-marker tier-${tier.toLocaleLowerCase()}${selection}${featured}${compact}" aria-label="${escapeHtml(label.title)}">
    <span class="lotto-map-marker-pin" aria-hidden="true">${symbol}</span>
    <span class="lotto-map-marker-caption"><strong>${escapeHtml(label.title)}</strong><small>${escapeHtml(label.detail)}</small></span>
  </span>`;
}

function clusterContent(cluster: Extract<MapEntry, { readonly kind: "cluster" }>): string {
  const count = cluster.storeCount > 99 ? "99+" : String(cluster.storeCount);
  return `<span class="lotto-map-cluster" aria-label="판매점 ${cluster.storeCount}곳"><strong>${count}</strong></span>`;
}

function userLocationContent(): string {
  return '<span class="lotto-map-user-location" aria-label="내 위치"><span></span></span>';
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!);
}

function readViewport(map: NaverMapInstance): LottoMapViewport | null {
  const bounds = map.getBounds();
  const southWest = bounds.getSW();
  const northEast = bounds.getNE();
  const viewport = { south: southWest.lat(), west: southWest.lng(), north: northEast.lat(), east: northEast.lng(), zoom: map.getZoom() };
  return Number.isFinite(viewport.south) && Number.isFinite(viewport.west) && Number.isFinite(viewport.north) && Number.isFinite(viewport.east) ? viewport : null;
}

function storeLabel(store: LottoStore, tier: StoreMarkerTier): { readonly title: string; readonly detail: string } {
  const history = `1등 ${store.firstPrizeCount}회 · 2등 ${store.secondPrizeCount}회`;
  if (tier === "RECENT_WINNER") return { title: `이번주 당첨 · ${store.name}`, detail: `이번주 당첨 · ${history}` };
  if (tier === "NEARBY_BEST") return { title: `★ ${store.name}`, detail: `현재 지도 1위 · ${history}` };
  return { title: store.name, detail: history };
}

function storeTier(store: LottoStore, bestStoreId: string | null, recentWinnerStoreIds: ReadonlySet<string>): StoreMarkerTier {
  if (recentWinnerStoreIds.has(store.id)) return "RECENT_WINNER";
  if (store.id === bestStoreId) return "NEARBY_BEST";
  if (store.firstPrizeCount > 0) return "FIRST_PRIZE";
  if (store.secondPrizeCount > 0) return "SECOND_PRIZE";
  return "OTHER";
}

function strongestStore(stores: readonly LottoStore[]): LottoStore | null {
  return stores.reduce<LottoStore | null>((best, store) => {
    if (best === null) return store;
    const storeScore = store.firstPrizeCount * 10_000 + store.secondPrizeCount;
    const bestScore = best.firstPrizeCount * 10_000 + best.secondPrizeCount;
    if (storeScore !== bestScore) return storeScore > bestScore ? store : best;
    return store.id.localeCompare(best.id) < 0 ? store : best;
  }, null);
}

function markerEntries(
  stores: readonly LottoStore[],
  api: NaverMapsApi,
  map: NaverMapInstance,
  recentWinnerStoreIds: ReadonlySet<string>,
): readonly MapEntry[] {
  const zoom = map.getZoom();
  const bestStore = strongestStore(stores);
  const bestStoreId = bestStore?.id ?? null;
  const asStoreEntry = (store: LottoStore): MapEntry => ({ kind: "store", store, tier: storeTier(store, bestStoreId, recentWinnerStoreIds) });
  if (zoom >= 12) return stores.map(asStoreEntry);
  // Android keeps the strongest place in the visible map separate from clusters so users can find a clear landmark even at a national zoom.
  const clusteredStores = bestStore === null ? stores : stores.filter((store) => store.id !== bestStore.id);
  const projection = map.getProjection();
  const groups = new Map<string, LottoStore[]>();
  for (const store of clusteredStores) {
    // Match Android's Clusterer.Builder.screenDistance(42.0): use the current
    // map projection rather than fixed degree cells.  A 42px cell keeps the
    // grouping stable across regions and makes the displayed count comparable
    // at the same zoom level on both surfaces.
    const offset = projection.fromCoordToOffset(new api.LatLng(store.latitude, store.longitude));
    const key = `${Math.floor(offset.x / 42)}:${Math.floor(offset.y / 42)}`;
    const group = groups.get(key);
    if (group) group.push(store);
    else groups.set(key, [store]);
  }
  const clusteredEntries = [...groups.values()].map((group) => {
    if (group.length === 1) return asStoreEntry(group[0]!);
    const latitude = group.reduce((sum, store) => sum + store.latitude, 0) / group.length;
    const longitude = group.reduce((sum, store) => sum + store.longitude, 0) / group.length;
    return { kind: "cluster" as const, latitude, longitude, storeCount: group.length };
  });
  return bestStore === null ? clusteredEntries : [asStoreEntry(bestStore), ...clusteredEntries];
}

function markerZIndex(tier: StoreMarkerTier, selected: boolean): number {
  if (selected) return 100;
  if (tier === "RECENT_WINNER") return 80;
  if (tier === "NEARBY_BEST") return 40;
  if (tier === "FIRST_PRIZE") return 30;
  if (tier === "SECOND_PRIZE") return 20;
  return 15;
}

export function NaverMapCanvas({
  clientId,
  stores,
  selected,
  recentWinnerStoreIds,
  userLocation,
  onSelect,
  onDismiss,
  onViewportChange,
}: {
  readonly clientId: string | undefined;
  readonly stores: readonly LottoStore[];
  readonly selected: LottoStore | null;
  readonly recentWinnerStoreIds: ReadonlySet<string>;
  readonly userLocation: GeolocationCoordinates | null;
  readonly onSelect: (store: LottoStore) => void;
  readonly onDismiss: () => void;
  readonly onViewportChange: (viewport: LottoMapViewport) => void;
}): JSX.Element {
  const element = useRef<HTMLDivElement>(null);
  const map = useRef<NaverMapInstance | null>(null);
  const api = useRef<NaverMapsApi | null>(null);
  const markers = useRef<NaverMarker[]>([]);
  const [state, setState] = useState<MapState>(clientId ? "loading" : "unavailable");
  const [attempt, setAttempt] = useState(0);
  const [zoom, setZoom] = useState(7);

  const publishViewport = useCallback(() => {
    if (!map.current) return;
    const viewport = readViewport(map.current);
    if (viewport) {
      setZoom(viewport.zoom);
      onViewportChange(viewport);
    }
  }, [onViewportChange]);

  useEffect(() => {
    let active = true;
    if (!clientId) { setState("unavailable"); return () => { active = false; }; }
    setState("loading");
    void loadSdk(clientId, attempt > 0, () => {
      if (active) setState("authentication-error");
    })
      .then((nextApi) => {
        if (!active || !element.current) return;
        api.current = nextApi;
        map.current = new nextApi.Map(element.current, {
          center: new nextApi.LatLng(36.5, 127.8),
          zoom: 7,
          minZoom: 6,
          maxZoom: 19,
          zoomControl: true,
          zoomControlOptions: { position: "TOP_RIGHT" },
        });
        nextApi.Event.addListener(map.current, "idle", publishViewport);
        nextApi.Event.addListener(map.current, "click", onDismiss);
        publishViewport();
        setState("ready");
      })
      .catch(() => { if (active) setState("error"); });
    return () => {
      active = false;
      markers.current.forEach((marker) => marker.setMap(null));
      markers.current = [];
      map.current = null;
    };
  }, [attempt, clientId, onDismiss, publishViewport]);

  useEffect(() => {
    if (state !== "ready" || !api.current || !map.current) return;
    markers.current.forEach((marker) => marker.setMap(null));
    const nextMarkers: NaverMarker[] = [];
    for (const entry of markerEntries(stores, api.current, map.current, recentWinnerStoreIds)) {
      if (entry.kind === "store") {
        const marker = new api.current.Marker({
          map: map.current,
          position: new api.current.LatLng(entry.store.latitude, entry.store.longitude),
          title: entry.store.name,
          // Keep the map readable at overview zooms. The store name/history is
          // available from zoom 13, while the icon and its accessible label
          // remain available at every zoom level.
          icon: { content: markerContent(entry.store, entry.tier, selected?.id === entry.store.id, zoom >= 13) },
          zIndex: markerZIndex(entry.tier, selected?.id === entry.store.id),
        });
        api.current.Event.addListener(marker, "click", () => onSelect(entry.store));
        nextMarkers.push(marker);
      } else {
        const marker = new api.current.Marker({
          map: map.current,
          position: new api.current.LatLng(entry.latitude, entry.longitude),
          title: `판매점 ${entry.storeCount}곳`,
          icon: { content: clusterContent(entry) },
          zIndex: 10,
        });
        api.current.Event.addListener(marker, "click", () => {
          map.current?.setCenter(new api.current!.LatLng(entry.latitude, entry.longitude));
          map.current?.setZoom(Math.min(zoom + 3, 19));
        });
        nextMarkers.push(marker);
      }
    }
    if (userLocation) {
      nextMarkers.push(new api.current.Marker({
        map: map.current,
        position: new api.current.LatLng(userLocation.latitude, userLocation.longitude),
        title: "내 위치",
        icon: { content: userLocationContent() },
      }));
    }
    markers.current = nextMarkers;
  }, [onSelect, recentWinnerStoreIds, selected?.id, state, stores, userLocation, zoom]);

  useEffect(() => {
    if (!selected || !api.current || !map.current) return;
    map.current.setCenter(new api.current.LatLng(selected.latitude, selected.longitude));
    map.current.setZoom(15);
  }, [selected]);

  useEffect(() => {
    if (!userLocation || !api.current || !map.current) return;
    map.current.setCenter(new api.current.LatLng(userLocation.latitude, userLocation.longitude));
    map.current.setZoom(15);
  }, [userLocation]);

  if (state === "unavailable") return <MapFallback title="지도를 준비하고 있어요" message="Naver Web 지도 설정이 완료되면 이곳에 실제 지도가 표시됩니다. 판매점 목록과 검색은 계속 사용할 수 있어요." />;
  if (state === "authentication-error") return <MapFallback title="NAVER 지도 사용 설정을 확인해 주세요" message="NAVER Cloud Maps 애플리케이션에서 Dynamic Map을 켜고 Web 서비스 URL에 https://lotto.appforge.synology.me 를 등록해야 해요. 판매점 목록과 검색은 계속 사용할 수 있어요." onRetry={() => setAttempt((value) => value + 1)} />;
  if (state === "error") return <MapFallback title="지도를 열지 못했어요" message="지도 제공자 연결을 다시 확인해 주세요. 판매점 목록에서 선택하면 주소와 길찾기를 이용할 수 있어요." onRetry={() => setAttempt((value) => value + 1)} />;
  return <div className="naver-map-canvas" aria-label="로또 판매점 지도"><div ref={element} className="naver-map-element" />{state === "loading" && <MapFallback title="지도를 불러오는 중" message="판매점 위치는 이 브라우저에서만 표시합니다." />}</div>;
}

function MapFallback({ title, message, onRetry }: { readonly title: string; readonly message: string; readonly onRetry?: () => void }): JSX.Element {
  return <div className="map-fallback" role="status"><b>{title}</b><span>{message}</span>{onRetry && <button className="map-retry" onClick={onRetry}>다시 시도</button>}</div>;
}
